import ConfirmationModal from "components/ConfirmationModal/ConfirmationModal";
import DispatchContext from "context/DispatchContext";
import StateContext from "context/StateContext";
import { checkIfAllowed, notificationMessage } from "global/helpers";
import Delete from "img/delete.svg";
import { useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import * as AppUrls from "constants/AppUrls";
import { Link, useHistory } from "react-router-dom";
import addIcon from "../../../img/add-icon.svg";
import {
  CREATE_SUCCESS_MSG,
  DELETE_SUCCESS_MSG,
  UPDATE_SUCCESS_MSG,
} from "constants/NotificationMsgs";
import { useForm } from "react-hook-form";
import {
  createProductLine,
  deleteProductLine,
  editProductLine,
  getProductLines,
} from "./InventorySetup.services";
import AgGrid from "elements/AgGrid";

const ProductLines = ({ match }) => {
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [refreshCounter, setRefreshCounter] = useState<number>(0);
  const [selectedId, setSelectedId] = useState<number>(null);
  const appDispatch = useContext(DispatchContext);
  const appState = useContext(StateContext);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const dataMutation = useMutation("data", getProductLines);
  const [displayIsRefreshing, setDisplayIsRefreshing] = useState(true);
  const [filter, setFilter] = useState({
    params: "",
  });
  const frmMethods = useForm({ mode: "onChange", reValidateMode: "onChange" });
  const { reset } = frmMethods;

  const columns = [
    {
      field: "productLine",
      headerName: "Product name",
    },
    {
      field: "description",
      headerName: "Description",
    },
    {
      field: "action",
      headerName: "Action",
      floatingFilter: false,
      filter: false,
      sortable: false,
      hide: !checkIfAllowed(appState.allowedUrls, "delete", match.path),
      onCellClicked: (params) => {
        onDeleteItemClicked(params.data);
      },
      cellRenderer: () => {
        return `
            <div class="btn text-danger">
              <img src=${Delete} alt="Delete Icon" />
              Delete 
            </div>
      `;
      },
    },
  ];

  const addProductLine = useMutation(createProductLine, {
    async onSuccess() {
      setDisplayIsRefreshing(true);
      setRefreshCounter((prev) => prev + 1);
      let notification = {
        variant: "success",
        msg: `Product Line ${CREATE_SUCCESS_MSG}`,
      };
      appDispatch({ type: "notification", value: notification });
      reset();
    },
    onError(error) {
      let notification = {
        variant: "danger",
        msg: notificationMessage(error, "problem adding product line"),
      };
      appDispatch({ type: "notification", value: notification });
    },
  });

  const updateProductLine = useMutation(editProductLine, {
    async onSuccess() {
      setDisplayIsRefreshing(true);
      setRefreshCounter((prev) => prev + 1);
      let notification = {
        variant: "success",
        msg: `Information ${UPDATE_SUCCESS_MSG}`,
      };
      appDispatch({ type: "notification", value: notification });
      setSelectedId(null);
      reset();
    },
    onError(error) {
      let notification = {
        variant: "danger",
        msg: notificationMessage(error, "problem editing product line"),
      };
      appDispatch({ type: "notification", value: notification });
    },
  });

  const removeItem = useMutation(deleteProductLine, {
    async onSuccess() {
      let notification = {
        variant: "success",
        msg: `Product line is ${DELETE_SUCCESS_MSG}`,
      };
      await appDispatch({ type: "notification", value: notification });
      setRefreshCounter((prev) => prev + 1);
    },
    onError(error) {
      let notification = {
        variant: "danger",
        msg: notificationMessage(error, "problem removing item product line"),
      };
      appDispatch({ type: "notification", value: notification });
    },
  });

  const history = useHistory();
  const onRowDoubleClicked = (e) => {
    const url = `${AppUrls.product_line}/${e.itemProductLineId}`;
    history.push({
      pathname: url,
      state: {
        id: e.itemProductLineId,
        name: e.productLine,
        description: e.description,
      },
    });
  };
  const handleDeleteItem = async (itemId) => {
    setDisplayIsRefreshing(false);
    await removeItem.mutate(itemId);
    setSelectedId(null);
    setShowDeleteModal(false);
  };

  const onDeleteItemClicked = (data) => {
    setSelectedId(data.itemProductLineId);
    setShowDeleteModal(true);
  };

  useEffect(() => {
    appDispatch({
      type: "loading",
      value:
        addProductLine.isLoading ||
        updateProductLine.isLoading ||
        removeItem.isLoading,
    });
  }, [
    addProductLine.isLoading,
    updateProductLine.isLoading,
    removeItem.isLoading,
    appDispatch,
  ]);

  const [dataGrid, setDataGrid] = useState(null);
  const [exportToExcelClicked, setExportToExcelClicked] =
    useState<boolean>(false);
  const [isLoadingGridExport, setIsLoadingGridExport] =
    useState<boolean>(false);

  useEffect(() => {
    if (dataGrid && dataGrid.length > 0) {
      if (dataMutation.isSuccess) {
        setIsLoadingGridExport(true);
        setExportToExcelClicked(true);
        let notification = {
          variant: "success",
          msg: "Preparing grid for export...",
        };
        appDispatch({ type: "notification", value: notification });
      }
    }
  }, [dataGrid, dataMutation.isSuccess]);

  useEffect(() => {
    appDispatch({ type: "loading", value: isLoadingGridExport });
  }, [appDispatch, isLoadingGridExport]);

  const displayFilter = () => {
    const params = searchTerm ? `searchQuery=${searchTerm}` : "";
    setFilter({
      params: params,
    });
    setDataGrid(null);
  };

  useEffect(() => {
    displayFilter();
  }, [searchTerm]);
  return (
    <>
      <div className="static-page">
        <div className="page-title page-title-editable">
          <p>Product Lines</p>
          <div>
            {checkIfAllowed(appState.allowedUrls, "add", match.path) && (
              <Link to={AppUrls.new_product_line} className="btn btn-primary">
                <img src={addIcon} className="me-2" alt="" /> New Product Line
              </Link>
            )}
          </div>
        </div>
        <div className="page-content-wrapper">
          <div className="filters">
            <div className="col-lg-3">
              <div className="page-search">
                <label className="label me-3">Search</label>
                <div className="input-group">
                  <span className="page-search-icon">
                    <i className="fa fa-search"></i>
                  </span>
                  <input
                    className="page-search-input ps-5"
                    type="text"
                    placeholder="Search"
                    onChange={(e) => setSearchTerm(e.target.value)}
                    value={searchTerm}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="row h-100">
            <div className=" grid-container">
              <div className="grid">
                <AgGrid
                  displayIsRefreshing={displayIsRefreshing}
                  columns={columns}
                  fetchMethod={dataMutation}
                  data={dataGrid}
                  filters={filter}
                  setDataGrid={setDataGrid}
                  setrowClicked={onRowDoubleClicked}
                  exportToExcel={exportToExcelClicked}
                  setExportToExcelClicked={setExportToExcelClicked}
                  setIsLoadingGridExport={setIsLoadingGridExport}
                  refreshCounter={refreshCounter}
                  autoSize={true}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        title="Delete Product Line"
        message="Are you sure you want to delete the item?"
        showModal={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => handleDeleteItem(selectedId)}
        cancelBtnTitle="Keep"
        confirmBtnTitle="Delete"
        type="confirmation-danger"
      />
    </>
  );
};

export default ProductLines;
